import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type LogEntry } from '../api';

function fmtDateTime(s: string): string {
  // stocké en UTC "YYYY-MM-DD HH:MM:SS"
  const d = new Date(s.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function LevelBadge({ level }: { level: string }) {
  const tone = level === 'error' ? 'tone-danger' : level === 'warning' ? 'tone-warn' : 'tone-info';
  const label = level === 'error' ? 'Erreur' : level === 'warning' ? 'Avertissement' : 'Info';
  return <span className={`status-badge ${tone}`}>{label}</span>;
}

export default function Journal() {
  const [items, setItems] = useState<LogEntry[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const [expanded, setExpanded] = useState<number | null>(null);
  const nav = useNavigate();

  function reload() {
    setLoading(true);
    setError(null);
    api.getLogs({ scope: scope === 'open' ? 'open' : undefined })
      .then((r) => { setItems(r.items); setOpenCount(r.openCount); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, [scope]);

  async function discuss(l: LogEntry) {
    const ctx = l.context ? `\n- Contexte : ${JSON.stringify(l.context)}` : '';
    const prefix =
      `J'ai une erreur/alerte technique dans le pipeline STM, aide-moi à comprendre et corriger.\n` +
      `- Source : ${l.source ?? '—'}${l.workflow ? ` (workflow ${l.workflow})` : ''}${l.node ? `, nœud « ${l.node} »` : ''}\n` +
      `- Niveau : ${l.level}\n` +
      `- ${l.title ?? 'Sans titre'}\n` +
      `- Message : ${l.message ?? '—'}${ctx}\n\nMa question : `;
    try {
      const conv = await api.createConversation();
      nav('/chat', { state: { conv: conv.id, draft: prefix } });
    } catch (e: any) { setError(e.message); }
  }

  async function toggleResolved(l: LogEntry) {
    try {
      await api.resolveLog(l.id, !l.resolved);
      reload();
    } catch (e: any) { setError(e.message); }
  }

  return (
    <div className="devis-page">
      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          <button className={`suivi-filter ${scope === 'open' ? 'active' : ''}`} onClick={() => setScope('open')}>À traiter</button>
          <button className={`suivi-filter ${scope === 'all' ? 'active' : ''}`} onClick={() => setScope('all')}>Tout</button>
        </div>
      </div>

      {loading && <div className="devis-empty">Chargement…</div>}
      {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
      {!loading && !error && items.length === 0 && <div className="devis-empty">🎉 Aucune entrée.</div>}

      {!loading && !error && items.length > 0 && (
        <div className="log-list">
          {items.map((l) => (
            <div key={l.id} className={`log-entry ${l.resolved ? 'resolved' : ''} lvl-${l.level}`}>
              <div className="log-head" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                <LevelBadge level={l.level} />
                <span className="log-title">{l.title || l.message || '(sans titre)'}</span>
                <span className="log-src">{l.source ?? ''}</span>
                <span className="log-date">{fmtDateTime(l.created_at)}</span>
              </div>
              {expanded === l.id && (
                <div className="log-body">
                  {l.workflow && <div className="log-kv"><span>Workflow</span><b>{l.workflow}{l.node ? ` · ${l.node}` : ''}</b></div>}
                  {l.message && <pre className="log-msg">{l.message}</pre>}
                  {l.context != null && <pre className="log-msg log-ctx">{JSON.stringify(l.context, null, 2)}</pre>}
                  <div className="log-actions">
                    <button className="devis-ask" onClick={() => discuss(l)}>💬 En discuter avec l'assistant</button>
                    {(l.context as any)?.gmail_link && <a className="devis-odoo" href={(l.context as any).gmail_link} target="_blank" rel="noopener noreferrer">↗ Ouvrir l'email</a>}
                    {l.execution_url && <a className="devis-odoo" href={l.execution_url} target="_blank" rel="noopener noreferrer">↗ Voir l'exécution n8n</a>}
                    <button className="btn-resolve" onClick={() => toggleResolved(l)}>{l.resolved ? 'Rouvrir' : '✓ Marquer résolu'}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
